import type { Communication } from '@medplum/fhirtypes';

/** Category coding on Communication for appointment-request messages */
export const APPOINTMENT_REQUEST_CATEGORY = {
  system: 'https://unify.health/fhir/identifier/messaging/message-type',
  code: 'appointment-request',
  display: 'Appointment request',
} as const;

/** Category for rescheduling an existing booked appointment via messaging */
export const APPOINTMENT_RESCHEDULE_CATEGORY = {
  system: 'https://unify.health/fhir/identifier/messaging/message-type',
  code: 'appointment-reschedule',
  display: 'Reschedule request',
} as const;

export const APPOINTMENT_REQUEST_DETAILS_EXT_URL = 'https://unify.health/fhir/identifier/messaging/appointmentRequestDetails';

export type AppointmentRequestType = 'new-appointment' | 'reschedule';

/** Structured payload stored as JSON in Communication.extension */
export interface AppointmentRequestDetailsPayload {
  /** Machine-readable flow discriminator (optional on legacy messages). */
  requestType?: AppointmentRequestType;
  providerName: string;
  serviceType: string;
  /** Human-readable preferred slot, e.g. "Monday, February 5, 2026 at 2:00 PM" */
  preferredWhen: string;
  note?: string;
  /** Portal / FHIR Appointment id the patient is asking to move */
  appointmentId?: string;
  /** Proposed appointment created when the client picks a new slot */
  proposedAppointmentId?: string;
  patientId?: string;
  providerId?: string;
  episodeOfCareId?: string;
  currentStart?: string;
  currentEnd?: string;
  requestedStart?: string;
  requestedEnd?: string;
  timeZone?: string;
  durationMinutes?: number;
  modality?: string;
  locationLabel?: string;
  /** Previous slot (for reschedule flows) */
  previousWhen?: string;
  /** Set when a provider accepts or declines a portal reschedule request. */
  providerDecision?: 'accept' | 'deny';
  respondedAt?: string;
}

export function isAppointmentRequestMessage(message: Communication): boolean {
  return (
    message.category?.some((c) =>
      c.coding?.some((co) => co.code === APPOINTMENT_REQUEST_CATEGORY.code),
    ) ?? false
  );
}

export function isAppointmentRescheduleMessage(message: Communication): boolean {
  return (
    message.category?.some((c) =>
      c.coding?.some((co) => co.code === APPOINTMENT_RESCHEDULE_CATEGORY.code),
    ) ?? false
  );
}

/** Appointment request or reschedule — same structured payload and extension URL */
export function isAppointmentFlowMessage(message: Communication): boolean {
  return isAppointmentRequestMessage(message) || isAppointmentRescheduleMessage(message);
}

export function parseAppointmentRequestDetails(message: Communication): AppointmentRequestDetailsPayload | null {
  const raw = message.extension?.find((e) => e.url === APPOINTMENT_REQUEST_DETAILS_EXT_URL)?.valueString;
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as AppointmentRequestDetailsPayload;
    if (isAppointmentRescheduleMessage(message) && !parsed.requestType) {
      parsed.requestType = 'reschedule';
    }
    if (isAppointmentRequestMessage(message) && !parsed.requestType) {
      parsed.requestType = 'new-appointment';
    }
    return parsed;
  } catch {
    return null;
  }
}

/** True when a provider accept/decline was written onto an appointment-flow message. */
export function hasProviderDecisionOnAppointmentFlowMessage(message: Communication): boolean {
  if (!isAppointmentFlowMessage(message)) return false;
  const decision = parseAppointmentRequestDetails(message)?.providerDecision;
  return decision === 'accept' || decision === 'deny';
}

/** Provider accept/decline on an existing reschedule request message (no separate reply message). */
export function parseRescheduleProviderDecision(message: Communication):
  | { decision: 'accept' | 'deny'; preferredWhen?: string; respondedAt?: string }
  | undefined {
  if (!isAppointmentRescheduleMessage(message)) return undefined;
  const details = parseAppointmentRequestDetails(message);
  if (details?.providerDecision !== 'accept' && details?.providerDecision !== 'deny') {
    return undefined;
  }
  return {
    decision: details.providerDecision,
    preferredWhen: details.preferredWhen,
    respondedAt: details.respondedAt,
  };
}

export function buildRescheduleDetailsPayload(params: {
  appointmentId: string;
  patientId: string;
  providerId: string;
  providerName: string;
  serviceType: string;
  episodeOfCareId?: string;
  currentStart: string;
  currentEnd: string;
  requestedStart?: string;
  requestedEnd?: string;
  proposedAppointmentId?: string;
  preferredWhen: string;
  previousWhen: string;
  timeZone?: string;
  durationMinutes?: number;
  modality?: string;
  locationLabel?: string;
  note?: string;
}): AppointmentRequestDetailsPayload {
  return {
    requestType: 'reschedule',
    appointmentId: params.appointmentId,
    patientId: params.patientId,
    providerId: params.providerId,
    providerName: params.providerName,
    serviceType: params.serviceType,
    episodeOfCareId: params.episodeOfCareId,
    currentStart: params.currentStart,
    currentEnd: params.currentEnd,
    requestedStart: params.requestedStart,
    requestedEnd: params.requestedEnd,
    proposedAppointmentId: params.proposedAppointmentId,
    preferredWhen: params.preferredWhen,
    previousWhen: params.previousWhen,
    timeZone: params.timeZone,
    durationMinutes: params.durationMinutes,
    modality: params.modality,
    locationLabel: params.locationLabel,
    ...(params.note ? { note: params.note } : {}),
  };
}

export function buildNewAppointmentDetailsPayload(params: {
  patientId: string;
  providerId: string;
  providerName: string;
  serviceType: string;
  episodeOfCareId: string;
  requestedStart: string;
  requestedEnd: string;
  proposedAppointmentId?: string;
  preferredWhen: string;
  timeZone?: string;
  durationMinutes?: number;
  note?: string;
}): AppointmentRequestDetailsPayload {
  return {
    requestType: 'new-appointment',
    patientId: params.patientId,
    providerId: params.providerId,
    providerName: params.providerName,
    serviceType: params.serviceType,
    episodeOfCareId: params.episodeOfCareId,
    requestedStart: params.requestedStart,
    requestedEnd: params.requestedEnd,
    proposedAppointmentId: params.proposedAppointmentId,
    preferredWhen: params.preferredWhen,
    timeZone: params.timeZone,
    durationMinutes: params.durationMinutes,
    ...(params.note ? { note: params.note } : {}),
  };
}
